/**
 * Cross-tab stale-flag for the cached polygon section calculation.
 *
 * The Cesium app and the calculation report live in separate browser tabs.
 * `idb-keyval` (used to persist the cached payload) doesn't emit
 * cross-tab change events, so a `BroadcastChannel` is what we use to push
 * stale-state updates from the Cesium tab to any open report tab.
 *
 * Within a single tab the exported `sectionCalculationStale` writable acts
 * as the source of truth. Cross-tab, every tab subscribes to the channel
 * and mirrors the incoming flag into its own copy of the store.
 *
 * Note: BroadcastChannel doesn't fire on the sender, so the broadcast
 * helpers below update the local store *and* post — both tabs end up with
 * the same value.
 */

import { writable, type Writable } from 'svelte/store';

import type { ISectionInfo } from '$lib/core/map/map-tools-store';
import type { ContextualizedCzmlResponse } from '$lib/models/IProjectModel';

const CHANNEL_NAME = 'polygon-section-calculation-stale';

type ChannelMessage =
	| { type: 'stale' }
	| { type: 'fresh' }
	| { type: 'recalculated'; info: ISectionInfo }
	| { type: 'czml'; response: ContextualizedCzmlResponse };

const channel: BroadcastChannel | null =
	typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;

export const sectionCalculationStale: Writable<boolean> = writable(false);

type RecalculatedHandler = (info: ISectionInfo) => void;
const recalculatedHandlers = new Set<RecalculatedHandler>();

type CzmlHandler = (response: ContextualizedCzmlResponse) => void;
const czmlHandlers = new Set<CzmlHandler>();

if (channel) {
	channel.addEventListener('message', (event: MessageEvent<ChannelMessage>) => {
		const message = event.data;
		if (message?.type === 'stale') sectionCalculationStale.set(true);
		else if (message?.type === 'fresh') sectionCalculationStale.set(false);
		else if (message?.type === 'recalculated') {
			sectionCalculationStale.set(false);
			for (const handler of recalculatedHandlers) handler(message.info);
		} else if (message?.type === 'czml') {
			for (const handler of czmlHandlers) handler(message.response);
		}
	});
}

/** Marks the cached calculation as stale, locally and in any open report tab. */
export function broadcastSectionCalculationStale(): void {
	sectionCalculationStale.set(true);
	channel?.postMessage({ type: 'stale' } satisfies ChannelMessage);
}

/** Marks the cached calculation as fresh — used after a successful regenerate. */
export function broadcastSectionCalculationFresh(): void {
	sectionCalculationStale.set(false);
	channel?.postMessage({ type: 'fresh' } satisfies ChannelMessage);
}

/**
 * Broadcasts a freshly-calculated `info` payload to any open Cesium tab so it
 * can mirror `runSectionCalculation`'s post-mutation side-effects (sectionInfo
 * replace, loadCaseSource → 'calculate', applyLoadCases, activeLoadCases). The
 * payload is the `info` field of the `/calculate` response — same shape as
 * `ISectionInfo`. Also clears the stale flag (subsumes a `fresh` broadcast).
 *
 * BroadcastChannel doesn't deliver to the sender, so handlers registered in
 * the *same* tab as the broadcast are not invoked. That's fine here — the
 * report tab has no Cesium viewer to update.
 */
export function broadcastSectionCalculationRecalculated(info: ISectionInfo): void {
	sectionCalculationStale.set(false);
	channel?.postMessage({ type: 'recalculated', info } satisfies ChannelMessage);
}

/**
 * Subscribes to recalculated-info broadcasts from another tab. Returns an
 * unsubscribe function. Used by the Cesium tab's path-mode effects to react
 * when the report tab regenerates the calculation.
 */
export function onSectionCalculationRecalculated(handler: RecalculatedHandler): () => void {
	recalculatedHandlers.add(handler);
	return () => {
		recalculatedHandlers.delete(handler);
	};
}

/**
 * Broadcasts a freshly-mutated CZML response (e.g. the JBD pole-diameter update
 * from the report tab) to any open Cesium tab so it can pipe the payload through
 * `handleContextualizedCzmlResponse` — redrawing the affected entities and,
 * when they intersect the active polygon, auto-refreshing the section `/info`.
 *
 * The payload is plain JSON (CZML packets), so it structured-clones over the
 * channel cleanly. BroadcastChannel doesn't deliver to the sender, so the
 * report tab (which has no Cesium viewer) is unaffected.
 */
export function broadcastContextualizedCzml(response: ContextualizedCzmlResponse): void {
	channel?.postMessage({ type: 'czml', response } satisfies ChannelMessage);
}

/**
 * Subscribes to CZML broadcasts from another tab. Returns an unsubscribe
 * function. Used by the Cesium tab's path-mode effects.
 */
export function onContextualizedCzml(handler: CzmlHandler): () => void {
	czmlHandlers.add(handler);
	return () => {
		czmlHandlers.delete(handler);
	};
}
